<template>
  <section class="datasets-edit-page">
    <header class="datasets-edit-page__header">
      <section class="datasets-edit-page__header-title">
        <h1>View / Edit Dataset</h1>
        <p class="datasets-edit-page--error" v-if="formHasError">
          Please complete all required fields.
        </p>
        <BaseButton
          class="datasets-edit-page__save-button"
          @click="updateDataset()"
          :disabled="pageIsReadOnly"
        >
          SAVE & EXIT
        </BaseButton>
      </section>
    </header>
    <section class="datasets-edit-page__sub-head">
      <section class="datasets-edit-page__meta-group">
        <div class="datasets-edit-page__meta-name">
          <label for="edit-company-id" class="datasets-edit-page__form-label">
            <span>Name:</span>
          </label>
          <input
            id="edit-company-id"
            :value="dataset.Name"
            type="text"
            disabled
            class="datasets-edit-page__display-field"
          />
        </div>
        <div class="datasets-edit-page__meta-used-in">
          <p class="datasets-edit-page__form-display-label">
            <span>Used in:</span>
          </p>
          <p class="datasets-edit-page__form-display-values">
            {{ dataset.UsedIn }}
          </p>
        </div>
      </section>
      <section class="datasets-edit-page__action-group">
        <BaseButton
          class="datasets-edit-page__action-button"
          title="Replace Dataset"
          color="dark-grey"
          :disabled="pageIsReadOnly"
          @click="replaceMyData()"
        >
          <IconReplace width="32" height="32" />
        </BaseButton>
        <BaseButton
          class="datasets-edit-page__action-button"
          title="Download Dataset"
          color="dark-grey"
          :disabled="isDownloadingDataset"
          @click="downloadDataset()"
        >
          <IconDownload width="28" height="28" />
        </BaseButton>
        <BaseButton
          class="datasets-edit-page__action-button"
          title="Delete Dataset"
          color="dark-grey"
          :disabled="pageIsReadOnly"
          @click="deleteDataset()"
        >
          <IconDelete width="28" height="28" />
        </BaseButton>
      </section>
    </section>
    <form @submit.prevent="submitForm()" class="datasets-edit-page__form">
      <section class="datasets-edit-page__form-section">
        <div class="datasets-edit-page__form-container">
          <label for="data-type" class="datasets-edit-page__form-label">
            <span>Data Type:</span>
            <IconAlertCircle v-if="errors.type" />
          </label>
          <select
            v-if="dataset.DataTypes && dataset.DataTypes.length > 0"
            id="data-type"
            v-model="form.type"
            :class="{
              'datasets-edit-page--is-invalid': !!errors.type,
            }"
            @change="validateFormTypeInput()"
            @blur="validateFormType()"
            :disabled="pageIsReadOnly"
          >
            <option
              v-for="dataType in dataset.DataTypes"
              :key="`data-type-${dataType['Text']}`"
              :value="dataType['Text']"
              :selected="dataType.Selected"
            >
              {{ dataType['Text'] }}
            </option>
          </select>
        </div>
        <div class="datasets-edit-page__form-container">
          <label for="data-base" class="datasets-edit-page__form-label">
            <span>Base:</span>
            <IconAlertCircle v-if="errors.base" />
          </label>
          <select
            v-if="dataset.Base && dataset.Base.length > 0"
            id="data-base"
            v-model="form.base"
            :class="{
              'datasets-edit-page--is-invalid': !!errors.base,
            }"
            @change="validateFormBaseInput()"
            @blur="validateFormBase()"
            :disabled="pageIsReadOnly"
          >
            <option
              v-for="dataBase in dataset.Base"
              :key="`data-base-${dataBase['Text']}`"
              :value="dataBase['Text']"
              :selected="dataBase.Selected"
            >
              {{ dataBase['Text'] }}
            </option>
          </select>
        </div>
        <div class="datasets-edit-page__form-container">
          <label for="data-units" class="datasets-edit-page__form-label">
            <span>Units:</span>
            <IconAlertCircle v-if="errors.units" />
          </label>
          <select
            v-if="dataset.Units && dataset.Units.length > 0"
            id="data-units"
            v-model="form.units"
            :class="{
              'datasets-edit-page--is-invalid': !!errors.units,
            }"
            @change="validateFormUnitsInput()"
            @blur="validateFormUnits()"
            :disabled="pageIsReadOnly"
          >
            <option
              v-for="dataUnit in dataset.Units"
              :key="`data-unit-${dataUnit['Text']}`"
              :value="dataUnit['Text']"
              :selected="dataUnit.Selected"
            >
              {{ dataUnit['Text'] }}
            </option>
          </select>
        </div>
      </section>
      <section class="datasets-edit-page__form-section">
        <div class="datasets-edit-page__form-container">
          <label for="data-description" class="datasets-edit-page__form-label">
            <span>Description:</span>
            <IconAlertCircle v-if="errors.description" />
          </label>
          <BaseTextarea
            id="data-description"
            v-model="form.description"
            type="text"
            :class="{
              'datasets-edit-page--is-invalid': !!errors.description,
            }"
            :invalid="errors.description"
            :disabled="pageIsReadOnly"
            @update:modelValue="validateFormDescriptionInput()"
            @blur="validateFormDescription()"
          />
        </div>
      </section>
      <section
        class="datasets-edit-page__submission-container datasets-edit-page__submission-container--notes-section"
      >
        <div
          class="datasets-edit-page__form-container datasets-edit-page__notes-container"
        >
          <label for="data-notes" class="datasets-edit-page__form-label">
            New notes
            <span class="datasets-edit-page__form-label--italic">
              (if there are any changes to your data history, please specify the
              reason for the change in data)
            </span>
            :
          </label>
          <BaseTextarea
            id="data-notes"
            v-model="form.notes"
            placeholder="Are there any significant changes to your business that would affect the data moving forward (new products, new efficiencies, pricing problems, etc.)? The more of these events we know about the more we are able to incorporate into our analysis.
If you answer “yes” to any of the below, please provide detailed notes to explain.
      - Are there “0s” in the data?
      - Was the data history revised in any way?
      - Are there missing months of data?
      - Is there a month or months of data that have deviated from forecast?"
            class="datasets-edit-page__form-notes"
            :disabled="pageIsReadOnly"
          />
          <p v-if="errors.notes" class="data-points-table__error">
            A note is required
          </p>
        </div>
      </section>
      <section class="datasets-edit-page__submission-container">
        <div class="datasets-edit-page__form-container">
          <label for="data-notes" class="datasets-edit-page__form-label">
            Previous notes:
          </label>
          <ul v-for="(note, index) in dataset.PreviousNotes">
            <li :key="index">{{ note }}</li>
          </ul>
        </div>
      </section>
      <section
        class="datasets-edit-page__invalid-table-entry-container"
        v-if="invalidEntries.length"
      >
        <p
          v-for="value in invalidEntries"
          :key="value"
          class="datasets-edit-page__invalid-table-text"
        >
          {{ value }} - Value in dataset is not a valid number.
        </p>
      </section>
      <section class="datasets-edit-page__form-container">
        <article class="data-points-table">
          <p
            v-if="dataPointsDuplicateDates.length > 0"
            class="data-points-table__error"
          >
            Duplicate dates are not allowed.
          </p>
          <p
            v-if="dataPointsDateGaps.length > 0"
            class="data-points-table__error"
          >
            Date gaps are not allowed.
          </p>
          <p
            v-if="dataPointsInvalidValues.length > 0"
            class="data-points-table__error"
          >
            All datapoints require a numeric value.
          </p>
          <header class="data-points-table__header">
            <div class="data-points-table__row data-points-table__row--header">
              <div class="data-points-table__cell">#</div>
              <div class="data-points-table__cell">
                <button
                  type="button"
                  class="data-points-table__date-button"
                  @click="sortDataPoints(true)"
                >
                  Date
                  <IconArrowUp width="16" :flipped="dateOrder === 'desc'" />
                </button>
              </div>
              <div class="data-points-table__cell">Value</div>
              <div class="data-points-table__cell">
                <BaseDropdown
                  button-color="green-mid"
                  button-text-color="white"
                  menu-color="white"
                  icon-fill="white"
                  button-size="md"
                  button-style="normal"
                  :psuedoIcon="true"
                  :disabled="pageIsReadOnly"
                >
                  <template #button>Add Row(s)</template>
                  <template #options>
                    <li>
                      <button type="button" @click="addRowsToDataset(1)">
                        Add 1 Month
                      </button>
                    </li>
                    <li>
                      <button type="button" @click="addRowsToDataset(3)">
                        Add 3 Months
                      </button>
                    </li>
                    <li>
                      <button type="button" @click="addRowsToDataset(6)">
                        Add 6 Months
                      </button>
                    </li>
                    <li>
                      <button type="button" @click="addRowsToDataset(12)">
                        Add 12 Months
                      </button>
                    </li>
                  </template>
                </BaseDropdown>
              </div>
            </div>
          </header>
          <section ref="tableBody" class="data-points-table__body">
            <div
              v-for="(dataPoint, index) in form.dataPoints"
              :key="`data-point-${dataPoint.MonthYear}`"
              class="data-points-table__row data-points-table__row--body"
              :class="{
                'data-points-table__row--highlighted':
                  newRowsToHighlight.includes(dataPoint.MonthYear),
                'data-points-table__row--error':
                  dataPointsDuplicateDates.includes(dataPoint.MonthYear) ||
                  dataPointsInvalidValues.includes(dataPoint.MonthYear) ||
                  dataPointsDateGaps.includes(dataPoint.MonthYear),
                'data-points-table__row--even': isEven(index),
                'data-points-table__row--odd': !isEven(index),
                'data-points-table__row--zero': dataPoint.Source == 0,
                'data-points-table__row--unsaved': isUnsaved(index),
              }"
            >
              <div class="data-points-table__cell">
                {{
                  dateOrder === 'asc'
                    ? displayDataPoints.length - index
                    : index + 1
                }}
              </div>
              <div class="data-points-table__cell">
                <!-- TODO: CALCULATE THE MAX DATE AND/OR THE MIN DATE -->
                <input
                  :id="`date-${dataPoint.MonthYear}`"
                  :value="dataPoint.MonthYearFormat"
                  type="date"
                  name="trip-start"
                  min="01/01/1990"
                  max="12/01/2025"
                  @change="updateMonthYear(index)"
                  :disabled="pageIsReadOnly"
                />
              </div>
              <div class="data-points-table__cell">
                <DatasetInput
                  :id="`data-points-table-source-${dataPoint.MonthYear}`"
                  :key="`dataset-input-${dataPoint.MonthYear}`"
                  v-model="dataPoint.Source"
                  :disabled="pageIsReadOnly"
                  @validate="validateRowSource(index)"
                />
              </div>
              <div class="data-points-table__cell">
                <BaseButton
                  color="dark-grey"
                  size="md"
                  class="dash-button"
                  @click="removeDataset(index)"
                  :disabled="pageIsReadOnly"
                >
                  <div class="dash-button__content-container">
                    <span class="dash-button__dash" />
                  </div>
                </BaseButton>
              </div>
            </div>
          </section>
        </article>
      </section>
    </form>
    <SiteNotifications ref="site-notifications" />
    <BaseDialog ref="confirmation-dialog" :padding="false" />
    <ItrLoading v-if="isSubmitting" infinite />
  </section>
</template>

<script>
  import _ from 'lodash';
  import axios from 'axios';
  import IconAlertCircle from '~/components/icon/IconAlertCircle.vue';
  import IconDelete from '~/components/icon/IconDelete.vue';
  import IconDownload from '~/components/icon/IconDownload.vue';
  import IconReplace from '~/components/icon/IconReplace.vue';
  import IconArrowUp from '~/components/icon/IconArrowUp.vue';
  import BaseButton from '~/components/base/BaseButton.vue';
  import BaseTextarea from '~/components/base/BaseTextarea.vue';
  import BaseDialog from '~/components/base/BaseDialog.vue';
  import BaseDropdown from '~/components/base/BaseDropdown.vue';
  import SiteNotifications from '~/components/site/SiteNotifications.vue';
  import ItrLoading from '~/components/ItrLoading.vue';
  import DatasetInput from '~/components/dataset/DatasetInput.vue';
  import { suppressDeprecationWarnings } from 'moment';

  export default {
    name: 'MyDatasetsEditPage',
    components: {
      IconAlertCircle,
      BaseButton,
      BaseTextarea,
      IconDelete,
      IconDownload,
      IconReplace,
      IconArrowUp,
      BaseDialog,
      BaseDropdown,
      SiteNotifications,
      ItrLoading,
      DatasetInput,
    },
    props: {
      user: {
        type: Object,
        required: true,
      },
      dataset: {
        type: Object,
        required: true,
      },
      isReadOnly: Boolean,
    },
    data: () => ({
      isSubmitting: false,
      dropdownData: null,
      dateOrder: 'desc',
      form: {
        id: '',
        doscode: '',
        isPortalData: false,
        name: '',
        description: '',
        type: '',
        base: '',
        units: '',
        notes: '',
        dataPoints: [],
      },
      errors: {
        description: false,
        type: false,
        base: false,
        units: false,
        notes: false,
      },
      newRowsToHighlight: [],
      isDownloadingDataset: false,
      isDeletingDataset: false,
      firstAttempt: true,
      firstZeroAttempt: true,
      zeroValueMonths: [],
      unsavedValues: -1,
      invalidEntries: [],
      missingDates: [],
    }),
    computed: {
      pageIsReadOnly() {
        return this.isReadOnly || (this.dataset && this.dataset.IsReadOnly);
      },
      formHasError() {
        return (
          this.errors.description ||
          this.errors.type ||
          this.errors.base ||
          this.errors.units ||
          this.errors.notes
        );
      },
      displayDataPoints() {
        const sortedArray = [...this.form.dataPoints].sort((a, b) => {
          // Order based on user selection, either ascending or descending
          return new Date(a.MonthYear) - new Date(b.MonthYear);
        });

        if (this.dateOrder === 'desc') {
          return sortedArray.reverse();
        }

        return sortedArray;
      },
      dataPointsDuplicateDates() {
        const dataPointDates = this.form.dataPoints.map(
          (dataPoint) => dataPoint.MonthYear.split('T')[0]
        );
        const duplicateRows = dataPointDates.filter(
          (dataPointDate, index) =>
            dataPointDates.indexOf(dataPointDate) !== index
        );

        return this.form.dataPoints
          .filter((dataPoint) =>
            duplicateRows.includes(dataPoint.MonthYear.split('T')[0])
          )
          .map((dataPoint) => dataPoint.MonthYear);
      },
      dataPointsDateGaps() {
        const dataPoints = this.form.dataPoints;

        return dataPoints
          .filter((currentDataPoint, index) => {
            const previousIndex = index - 1;
            const nextIndex = index + 1;
            const lastIndex = dataPoints.length - 1;
            const currentDate = new Date(currentDataPoint.MonthYear);
            currentDate.setHours(0, 0, 0, 0);
            const currentYear = currentDate.getYear();
            const currentMonth = currentDate.getMonth();

            if (previousIndex >= 0) {
              const previousDate = new Date(
                dataPoints[previousIndex].MonthYear
              );
              previousDate.setHours(0, 0, 0, 0);
              const previousYear = previousDate.getYear();
              const previousYearDifference = Math.abs(
                currentYear - previousYear
              );

              if (previousYearDifference > 1) return true;

              const previousMonth = previousDate.getMonth();

              if (
                this.dateOrder === 'desc' &&
                previousYearDifference === 1 &&
                currentMonth === 11 &&
                previousMonth === 0
              )
                return false;
              if (
                this.dateOrder === 'asc' &&
                previousYearDifference === 1 &&
                currentMonth === 0 &&
                previousMonth === 11
              )
                return false;

              const previousMonthDifference = Math.abs(
                currentMonth - previousMonth
              );
              if (previousMonthDifference > 1) return true;
            }

            if (nextIndex <= lastIndex) {
              const nextDate = new Date(dataPoints[nextIndex].MonthYear);
              nextDate.setHours(0, 0, 0, 0);
              const nextYear = nextDate.getYear();
              const nextYearDifference = Math.abs(currentYear - nextYear);

              if (nextYearDifference > 1) return true;

              const nextMonth = nextDate.getMonth();

              if (
                this.dateOrder === 'desc' &&
                nextYearDifference === 1 &&
                currentMonth === 0 &&
                nextMonth === 11
              )
                return false;
              if (
                this.dateOrder === 'asc' &&
                nextYearDifference === 1 &&
                currentMonth === 11 &&
                nextMonth === 0
              )
                return false;

              const nextMonthDifference = Math.abs(currentMonth - nextMonth);
              if (nextMonthDifference > 1) return true;
            }

            return false;
          })
          .map((dataPoint) => dataPoint.MonthYear);
      },
      dataPointsInvalidValues() {
        return this.form.dataPoints
          .filter((dataPoint) => {
            return (
              dataPoint.Source === null ||
              dataPoint.Source === undefined ||
              dataPoint.Source === ''
            );
          })
          .map((dataPoint) => dataPoint.MonthYear);
      },
    },
    async created() {
      this.form.id = this.dataset.ID;
      this.form.doscode = encodeURIComponent(this.dataset.DOSCode);
      this.form.isPortalData = this.dataset.IsPortalData;
      this.form.name = this.dataset.Name;
      this.form.description = this.dataset.Description || '';
      this.form.type = this.dataset.DataTypes.filter(
        (type) => type.Selected === true
      )[0].Text;
      this.form.base = this.dataset.Base.filter(
        (base) => base.Selected === true
      )[0].Text;
      this.form.units = this.dataset.Units.filter(
        (unit) => unit.Selected === true
      )[0].Text;
      this.form.dataPoints = this.dataset.MetricData.map((dataPoint) => {
        const newDate = new Date(dataPoint.MonthYear);
        return {
          MonthYear: dataPoint.MonthYear,
          MonthYearFormat: newDate.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }),
          Source: dataPoint.Source,
        };
      });
      this.sortDataPoints();
    },
    methods: {
      sortDataPoints(toggle = false) {
        if (toggle) {
          this.dateOrder = this.dateOrder === 'asc' ? 'desc' : 'asc';
        }

        this.form.dataPoints.sort((a, b) => {
          // Order based on user selection, either ascending or descending
          return new Date(a.MonthYear) - new Date(b.MonthYear);
        });

        if (this.dateOrder === 'desc') {
          return this.form.dataPoints.reverse();
        }
      },
      async updateDataset() {
        if (this.pageIsReadOnly) return;

        this.validateFormDescription();
        this.validateFormType();
        this.validateFormBase();
        this.validateFormUnits();
        this.validateFormNotes();

        if (
          this.formHasError ||
          this.dataPointsDuplicateDates.length > 0 ||
          this.dataPointsDateGaps.length > 0 ||
          this.dataPointsInvalidValues.length > 0
        )
          return;

        const dataPointDate = new Date(this.form.dataPoints[0].MonthYear);
        const latestEntry = new Date(
          dataPointDate.getFullYear(),
          dataPointDate.getMonth(),
          dataPointDate.getDate(),
          0,
          0,
          0,
          0
        );

        const currentLocalDate = new Date();
        const currentDate = new Date(
          currentLocalDate.getFullYear(),
          currentLocalDate.getMonth(),
          currentLocalDate.getDate(),
          0,
          0,
          0,
          0
        );
        const futureEntry = currentDate < latestEntry;

        if (this.firstZeroAttempt) {
          this.form.dataPoints.forEach((point) => {
            if (point.Source == 0) {
              const newDate = new Date(point.MonthYear);
              this.zeroValueMonths.push(
                `${newDate.getMonth() + 1}-${newDate.getFullYear()}`
              );
            }
          });
        }

        if (
          this.zeroValueMonths.length &&
          futureEntry &&
          this.firstAttempt &&
          this.firstZeroAttempt
        ) {
          const response = await this.$refs['confirmation-dialog'].prompt({
            title:
              '<span style="font-size: 20px; font-weight: 700">Some of the entries in your submission require attention.</span>',
            message: `<span style="color: var(--itr-blue)"><ul>
                  <li>The following months have "0" listed in your excel sheet: <em>${this.zeroValueMonths.join(', ')}</em>.</li>
                  <li>Some entries list future months.</li>
              </ul></span>`,
            message2: `<span style="color: var(--itr-blue)">If this was intentional, you may continue with submitting your Dataset Request. If any of this was not intentional, please edit your excel file and reupload.</span>`,
            confirmText: 'Okay',
            level: 'success',
            size: 'sm',
          });
          if (response) {
            this.firstAttempt = false;
            this.firstZeroAttempt = false;
            return;
          }
        }

        if (futureEntry && this.firstAttempt) {
          const response = await this.$refs['confirmation-dialog'].prompt({
            title:
              '<span style="font-size: 20px; font-weight: 700">We found data entries listed in future months of your submission.</span>',
            message: `<span style="color: var(--itr-blue)">If this was intentional, please be sure to include the reasoning behind this inclusion in the "notes" section of your submission.</span>`,
            message2: `<span style="color: var(--itr-blue)">If any of this was not intentional, please edit your excel file and reupload.</span>`,
            confirmText: 'Okay',
            level: 'success',
          });
          if (response) {
            this.firstAttempt = false;
            return;
          }
        }

        if (this.zeroValueMonths.length && this.firstZeroAttempt) {
          const response = await this.$refs['confirmation-dialog'].prompt({
            title:
              '<span style="font-size: 20px; font-weight: 700">We found zeroes in some of the fields of your submission.</span>',
            message: `<span style="color: var(--itr-blue)">The following months have "0" listed in your excel sheet: <em>${this.zeroValueMonths.join(', ')}</em>.</span>`,
            message2: `<span style="color: var(--itr-blue)">If this was intentional, you may continue with submitting your Dataset Request. If any of this was not intentional, please edit your excel file and reupload.</span>`,
            confirmText: 'Okay',
            level: 'success',
          });
          if (response) {
            this.firstZeroAttempt = false;
            return;
          }
        }

        try {
          this.isSubmitting = true;
          const submissionData = {
            id: this.form.id,
            isPortalData: this.form.isPortalData,
            doscode: this.form.doscode,
            name: this.form.name,
            description: this.form.description,
            type: this.form.type,
            base: this.form.base,
            units: this.form.units,
            notes: this.form.notes,
            dataPoints: [],
          };
          if (submissionData.type === 'Select One') {
            submissionData.type = '';
          }
          if (submissionData.base === 'Select One') {
            submissionData.base = '';
          }
          if (submissionData.units === 'Select One') {
            submissionData.units = '';
          }
          this.form.dataPoints.forEach((dataPoint) => {
            submissionData.dataPoints.push({
              MonthYear: dataPoint.MonthYear,
              Source: dataPoint.Source.replace(/\$/g, ''),
            });
          });
          await axios.post('/dashboard/dataset/update', submissionData);
          const url = window.location.href;
          if (url.includes('datacast')) {
            window.location.replace('/datacast/datasets?edit=success');
          } else if (url.includes('alliance')) {
            window.location.replace('/alliance/datasets?edit=success');
          } else {
            window.location.replace('/dashboard/datasets?edit=success');
          }
        } catch (err) {
          this.isSubmitting = false;
          this.$refs['site-notifications'].addNotification({
            message: 'Oh no! Something went wrong. Please try again.',
            color: 'danger',
          });
        }
      },
      validateFormDescription() {
        this.errors.description = false;
        if (this.dataset.IsPortalData) return;
        if (this.form.description.length <= 0) {
          this.errors.description = true;
        }
      },
      validateFormDescriptionInput: _.debounce(
        function validateFormDescriptionInput() {
          this.validateFormDescription();
        },
        250
      ),
      validateFormType() {
        this.errors.type = false;
        if (this.dataset.IsPortalData) return;
        if (
          !this.form.type ||
          this.form.type === '' ||
          this.form.type === 'Select One'
        ) {
          this.errors.type = true;
        }
      },
      validateFormTypeInput: _.debounce(function validateFormTypeInput() {
        this.validateFormType();
      }, 250),
      validateFormBase() {
        this.errors.base = false;
        if (this.dataset.IsPortalData) return;
        if (
          !this.form.base ||
          this.form.base === '' ||
          this.form.base === 'Select One'
        ) {
          this.errors.base = true;
        }
      },
      validateFormBaseInput: _.debounce(function validateFormBaseInput() {
        this.validateFormBase();
      }, 250),
      validateFormUnits() {
        this.errors.units = false;
        if (this.dataset.IsPortalData) return;
        if (
          !this.form.units ||
          this.form.units === '' ||
          this.form.units === 'Select One'
        ) {
          this.errors.units = true;
        }
      },
      validateFormUnitsInput: _.debounce(function validateFormUnitsInput() {
        this.validateFormUnits();
      }, 250),
      async addRowsToDataset(numberOfRows) {
        for (let i = 0; i < numberOfRows; i++) {
          await this.addRowToDataset();
        }

        this.$refs['site-notifications'].addNotification({
          message: 'New row(s) added to the dataset.',
          color: 'success',
        });
      },
      validateFormNotes() {
        this.errors.notes = false;
        if (this.dataset.IsPortalData) return;
        if (this.form.notes.length <= 0) {
          this.errors.notes = true;
        }
      },
      async addRowToDataset() {
        if (this.pageIsReadOnly) return;

        let newDate = null;

        if (this.form.dataPoints.length <= 0) {
          newDate = new Date();
        } else {
          let lastDatasetIndex =
            this.dateOrder === 'desc' ? 0 : this.form.dataPoints.length - 1;
          const lastDataset = this.form.dataPoints[lastDatasetIndex];
          const lastDatasetDate = new Date(lastDataset.MonthYear);
          newDate = new Date(
            lastDatasetDate.getFullYear(),
            lastDatasetDate.getMonth() + 1,
            lastDatasetDate.getDate(),
            0,
            0,
            0,
            0
          );
        }
        const newDataPoint = {
          MonthYear:
            newDate.toLocaleDateString('sv-SE', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
            }) + 'T00:00:00',
          MonthYearFormat: newDate.toLocaleDateString('sv-SE', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
          }),
          Source: 0,
        };

        if (this.missingDates.length) {
          let dataPoint = this.missingDates.shift();
          dataPoint.Source = 0;
          this.form.dataPoints.splice(dataPoint.index, 0, dataPoint);
        } else {
          if (this.dateOrder === 'desc') {
            this.form.dataPoints.unshift(newDataPoint);
          } else {
            this.form.dataPoints.push(newDataPoint);
          }
          if (this.unsavedValues < 0) {
            this.unsavedValues = 0;
          } else {
            this.unsavedValues++;
          }
        }

        this.newRowsToHighlight.push(newDataPoint.MonthYear);

        await this.$nextTick();

        setTimeout(() => {
          // GIVE THE USER TIME TO SEE THE HIGHLIGHED ROW AND THEN REMOVE IT
          this.newRowsToHighlight = this.newRowsToHighlight.filter(
            (highlightedMonthYear) =>
              highlightedMonthYear !== newDataPoint.MonthYear
          );
        }, 2000);
      },
      removeDataset(dataPointIndex) {
        if (this.pageIsReadOnly) return;

        if (
          dataPointIndex !== 0 &&
          dataPointIndex !== this.form.dataPoints.length - 1
        ) {
          const dataPoint = this.form.dataPoints[dataPointIndex];
          this.missingDates.push({
            index: dataPointIndex,
            MonthYearFormat: dataPoint.MonthYearFormat,
            MonthYear: dataPoint.MonthYear,
          });
        }

        this.form.dataPoints.splice(dataPointIndex, 1);

        if (!this.dataPointsDateGaps.length) {
          this.missingDates = [];
        }
        this.unsavedValues--;
      },
      updateMonthYear(dataPointIndex) {
        const dataPoint = this.form.dataPoints[dataPointIndex];

        if (!dataPoint) {
          return;
        }

        const newDate = new Date(dataPoint.MonthYear);
        this.form.dataPoints[dataPointIndex].MonthYear =
          `${newDate.getFullYear()}-${newDate.getMonth()}-${newDate.getDate()}T00:00:00`;
      },
      validateRowSource(dataPointIndex) {
        const dataPoint = this.form.dataPoints[dataPointIndex];

        if (!dataPoint) {
          return;
        }

        const invalidEntryIndex = this.invalidEntries.indexOf(
          dataPoint.MonthYearFormat
        );
        const cleanedValue = dataPoint.Source.replace(/[^\d.-]/g, '');

        if (isNaN(cleanedValue) && invalidEntryIndex === -1) {
          this.invalidEntries.push(dataPoint.MonthYearFormat);
          return;
        }

        this.invalidEntries.splice(invalidEntryIndex, 1);
      },
      async downloadDataset() {
        try {
          this.isDownloadingDataset = true;
          const { data } = await axios.post(
            '/dashboard/dataset/download',
            {
              dataset_id: this.dataset.ID,
              is_portal_data: this.dataset.IsPortalData,
              doscode: this.form.doscode,
            },
            {
              responseType: 'arraybuffer',
            }
          );

          const fileName = this.dataset.Name
            ? this.dataset.Name.replaceAll(/[^\w\s]/gi, '').replaceAll(' ', '')
            : `data-export`;
          const fileURL = window.URL.createObjectURL(new Blob([data]));
          const fileLink = document.createElement('a');
          fileLink.id = 'excel_' + this.dataset.encrypted_id;
          fileLink.href = fileURL;
          fileLink.setAttribute('download', `${fileName}.xlsx`);
          document.body.appendChild(fileLink);
          fileLink.click();

          fileLink.remove();
        } catch (err) {
          // TODO: HANDLE ERROR - This is just silencing the error
        } finally {
          this.isDownloadingDataset = false;
        }
      },
      async deleteDataset() {
        if (this.pageIsReadOnly) return;
        let message = '';
        let message2 = '';
        if (this.form.isPortalData) {
          message = `<span>If you continue, the following dataset(s) will be <strong>deleted permanently.</strong></span>
                  <ul style="margin-top: 1rem; margin-bottom: 1rem">
                    <li><strong>${this.dataset.Name}</strong></li>
                  </ul>`;
          message2 =
            'Once permanently deleted, datasets are no longer available in My Data or DataCast. You will also no longer have access to any associated Saved Charts in DataCast, if applicable. There will be no option to reverse this action.';
        } else {
          let dataset = this.dataset.UsedIn
            ? `${this.dataset.Name} - <em>Used in ${this.dataset.UsedIn}</em>`
            : this.dataset.Name;
          message = `<span>If you continue, a request will be sent to ITR Economics to move the following dataset(s) to <strong>archive.</strong></span>
                  <ul style="margin-top: 1rem; margin-bottom: 1rem">
                    <li><strong>${dataset}</strong></li>
                  </ul>`;
          message2 =
            'Once archived, datasets are no longer available for you to view or edit in My Data. You will also no longer have access to any associated Saved Charts in DataCast, if applicable. There will be no option to reverse this action. Please note that this action will not immediately be apparent on our website. An economist will review your request and may reach out to you with questions.';
        }
        const response = await this.$refs['confirmation-dialog'].prompt({
          title: 'Are you sure?',
          message: message,
          message2: message2,
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          level: 'danger',
        });

        // IF THE USER CANCELLED THE DELETE, DO NOTHING
        if (!response) return;

        try {
          this.$emit('deleted-dataset', this.dataset.ID);
          await axios.post('/dashboard/dataset/delete', {
            dataset_id: this.dataset.ID,
            is_portal_data: this.dataset.IsPortalData,
            doscode: this.form.doscode,
          });
          window.location.replace(`/dashboard/datasets`);
        } catch (err) {
          this.$emit('failed-to-deleted-dataset', this.dataset.ID);
        }
      },
      isEven(index) {
        const num = parseInt(index);
        if (this.unsavedValues % 2 === 0) {
          return num % 2 === 0 ? false : true;
        }
        return num % 2 === 0 ? true : false;
      },
      isUnsaved(index) {
        if (this.dateOrder === 'desc') {
          return index < this.unsavedValues + 1;
        } else {
          let total = this.form.dataPoints.length;
          let lastItemsCount = this.unsavedValues + 1;
          return index >= total - lastItemsCount && index < total;
        }
      },
      replaceMyData() {
        if (this.pageIsReadOnly) return;
        window.location.href = `/dashboard/datasets/upload/${this.dataset.encrypted_id}`;
      },
    },
  };
</script>

<style lang="scss" scoped>
  .datasets-edit-page {
    padding: 15px 45px 45px 45px;

    &--is-invalid {
      border-color: #eb5757 !important;
    }

    &__form-checkbox-content {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 10px;
    }
    &__form-checkbox-content-label {
      white-space: nowrap;
    }

    &__header {
      display: flex;
      flex-direction: column;
      width: 100%;
      z-index: 0;
    }
    &__header-title {
      display: flex;
      flex-direction: column;
      align-items: center;
      width: 100%;
      padding: 15px;

      h1 {
        margin: 0;
      }
      p {
        flex: 1 auto;
        padding: 0 15px;
      }
    }
    &__sub-head {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      width: 100%;
      padding: 15px;
      gap: 15px;
    }
    &__meta-group {
      display: flex;
      flex-direction: row;
      align-items: flex-start;
      width: 100%;
      padding: 15px;
      gap: 15px;
    }
    &__meta-name {
      min-width: 300px;
    }
    &__meta-used-in {
      min-width: 300px;
    }
    &__action-group {
      display: flex;
      justify-content: flex-end;
      flex-direction: row;
      width: 100%;
      gap: 10px;
    }
    &__action-button {
      display: flex;
      justify-content: center;
      align-items: center;
      width: fit-content;
      cursor: pointer;
      min-width: 120px;
    }
    &__save-button {
      margin-left: auto;
    }
    &__display-field {
      color: #a7a9ab !important;
      border: none !important;
      padding: 0 !important;
      min-height: fit-content !important;
      height: 100%;
    }
    &__form {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    &__form-label {
      font-size: 16px;
      flex: 1;

      &--no-margin {
        margin: 0;
      }

      &--italic {
        font-style: italic;
      }
    }
    &__form-section {
      display: flex;
      flex-direction: column;
      width: 100%;
    }
    &__form-container {
      display: flex;
      flex-direction: column;
      width: 100%;
      padding: 15px;
    }
    &__form-notes {
      min-height: 135px;
    }
    &__submission-container {
      display: flex;
      flex-direction: column;
      width: 100%;

      &--notes-section {
        width: 100%;
        flex-direction: row;
      }
    }
    &__notes-container {
      flex: 1 auto;
      width: 100%;
    }
    &__form-submit-button {
      align-self: center;
      width: fit-content;
      margin: 10px 0;
    }
    &--error {
      color: #eb5757;
      font-style: italic;
      font-size: 14px;
    }

    hr {
      border: 3px solid #3075a4;
      margin: 25px 0;
    }

    &__form-display-label {
      font-size: 16px;
      font-weight: 700;
      margin-bottom: 10px;
    }
    &__form-display-values {
      color: var(--itr-grey-dark);
      margin: 0;
    }
    &__invalid-table-text {
      color: #eb5757;
      font-size: 14px;
      font-weight: 700;
      margin-top: 4px;
    }
  }

  .data-points-table {
    width: 100%;

    &__date-button {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: transparent;
      border: none;
      cursor: pointer;
      font-weight: 600;
      font-size: 16px;
      color: #17355c;
    }

    &__error {
      color: #eb5757;
      font-style: italic;
      font-size: 14px;
      margin: 0;
      padding: 0 15px;
    }

    &__header {
      border-top: 2px #dbdcde solid;
      width: 100%;
      font-weight: 600;
      font-size: 16px;
      color: #17355c;
      z-index: 10;
    }
    &__body {
      width: 100%;
      max-height: 65vh;
      overflow-y: auto;
      overflow-x: hidden;
      scroll-behavior: smooth;
      background-color: #dbdcde;
    }
    &__row {
      display: grid;
      gap: 16px;
      grid-template-columns: 1fr 4fr 4fr 2fr;
      width: 100%;
      padding: 8px 0;

      &--body {
        background-color: #dbdcde;
        transform: scale(1);
        transition:
          background-color 500ms,
          transform 350ms;
      }
      &--even {
        background-color: var(--itr-grey-light);
        &:hover {
          background-color: var(--itr-grey);
        }
      }

      &--odd {
        background-color: var(--itr-white);
        &:hover {
          background-color: var(--itr-grey);
        }
      }

      &--zero,
      &--unsaved {
        background-color: var(--itr-grey);
        &:hover {
          background-color: var(--itr-grey);
        }
      }

      &--highlighted {
        background-color: rgba(#09a684, 0.7);
        transform: scale(1.01);
        transition:
          background-color 500ms,
          transform 350ms;
      }
      &--error {
        background-color: #cc0000;
        background-color: #eb5757;
        // transform: scale(1.01);
        transition: background-color 500ms;
      }

      &--header {
        z-index: 10;
      }
    }
    &__cell {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .dash-button {
    position: relative;
    width: 35px;
    height: 35px;
    padding: 0;

    &__content-container {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      width: 100%;
      height: 100%;

      display: flex;
      justify-content: center;
      align-items: center;
    }
    &__dash {
      display: block;
      box-sizing: border-box;
      width: 15px;
      height: 5px;
      background-color: #fff;
    }
  }

  @include screen-lg {
    .datasets-edit-page {
      &__header-title,
      &__form-section {
        flex-direction: row;
      }
    }
  }
</style>
